import { getMockFlightSchoolAdmins } from '@/mock/FlightSchoolAdmins';
import { fsumClient } from '@/services/FsumHttpClient';
import type FlightSchool from '@/types/FlightSchool';
import type { FlightSchoolAdmin, RequestableFlightSchoolAdmin } from '@/types/FlightSchoolAdmin';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import type { AccountEmailValidationReponse } from '@/types/Response/AdminResponse';

async function getMockAdmins(): Promise<FlightSchoolAdmin[]> {
    return Promise.resolve(getMockFlightSchoolAdmins());
}

async function getSchoolAdmins(school: FlightSchool): Promise<FlightSchoolAdmin[]> {
    return fsumClient.get(`/v1/flight-schools/${school.id}/admins`);
}

async function getCurrentAdminInfo(): Promise<{ name: string, email: string }> {
    return fsumClient.get('/v1/auth/profile');
}

async function updateAdminEmailSubscriptions(
    school: FlightSchool,
    adminId: number,
    emailSubscriber: boolean,
): Promise<void> {
    return fsumClient.put(`/v1/flight-schools/${school.id}/admins/${adminId}/email-subscriptions`, {
        emailSubscriptions: school.courses.map((course: FlightSchoolCourse) => ({
            certificate: course.certificate.uuid,
            emailSubscriber,
        })),
    });
}

/**
 * Add one or more admins to a flight school and assign them to one or more courses.
 * Includes the option to subscribe the admin to email notifications for each course.
 */
async function addCourseAdmins(
    school: FlightSchool,
    admins: RequestableFlightSchoolAdmin[],
): Promise<void> {
    return fsumClient.post(`/v1/flight-schools/${school.id}/admins`, {
        admins: admins.map((admin: RequestableFlightSchoolAdmin) => ({
            email: admin.email,
            courses: admin.courses.map((course: FlightSchoolCourse) => course.certificate.uuid),
            emailSubscriber: admin.emailSubscriber,
        })),
    });
}

async function addCourseAdmin(
    school: FlightSchool,
    course: FlightSchoolCourse,
    email: string,
): Promise<void> {
    return fsumClient.post(`/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/admins`, { email });
}

/**
 * Remove an admin from a course. Does not remove the admin from the flight school.
 * @todo: Use the delete admin method to remove the admin from the flight school.
 * If there is only a single course then this method should not be used.
 */
async function removeAdminCourse(
    school: FlightSchool,
    course: FlightSchoolCourse,
    adminId: number,
): Promise<void> {
    return fsumClient.delete(`/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/admins/${adminId}`);
}

async function deleteCourseAdmin(
    school: FlightSchool,
    adminId: number,
): Promise<void> {
    return fsumClient.delete(`/v1/flight-schools/${school.id}/admins/${adminId}`);
}

async function validateAccountExists(
    email: string,
    school?: FlightSchool,
): Promise<AccountEmailValidationReponse> {
    return fsumClient.post(`/v1/flight-schools/${school?.id ?? 0}/admins/valid`, { email });
}

export default {
    getMockAdmins,
    getSchoolAdmins,
    getCurrentAdminInfo,
    updateAdminEmailSubscriptions,
    addCourseAdmin,
    addCourseAdmins,
    deleteCourseAdmin,
    removeAdminCourse,
    validateAccountExists,
};
